<!-- Backdrop -->
<div (click)="close()" class="fixed inset-0 z-40 bg-black/60 backdrop-blur-xs transition-opacity duration-300 ease-out" [class.opacity-100]="modalService.isOpen()"
  [class.opacity-0]="!modalService.isOpen()" [class.pointer-events-auto]="modalService.isOpen()" [class.pointer-events-none]="!modalService.isOpen()"></div>

<!-- Bottom Modal Container -->
<div class="fixed bottom-0 left-0 right-0 z-50 w-full max-w-2xl mx-auto bg-surface rounded-t-2xl shadow-2xl transition-transform duration-300 ease-out flex flex-col max-h-[85vh]"
  [class.translate-y-0]="modalService.isOpen()" [class.translate-y-full]="!modalService.isOpen()">
  <!-- Modal Header -->
  <div class="flex justify-between items-center px-5 py-4 border-b border-border bg-surface shrink-0">
    <div>
      <div class="flex items-center gap-2 flex-wrap">
        <h2 class="text-lg font-bold leading-tight" [class.text-main]="isSellable()" [class.text-muted]="!isSellable()">
          {{ getDisplayText(product()?.Name) }}
        </h2>
        @if (!isSellable()) {
        <span class="text-[10px] font-bold text-red-600 bg-red-100 dark:bg-red-950/40 dark:text-red-400 px-2 py-0.5 rounded-full whitespace-nowrap">
          {{ 'PRODUCT.UNAVAILABLE' | translate }}
        </span>
        }
      </div>
      <span class="text-xs font-semibold" [class.text-primary]="isSellable()" [class.text-muted]="!isSellable()">
        {{ productEffectiveBasePrice() * (1 + productEffectiveTaxPercent() / 100) | formatPrice:currencyPrecision:companyCurrency }}
      </span>
    </div>

    <button (click)="close()" class="w-8 h-8 flex items-center justify-center text-muted bg-surface-hover rounded-full hover:bg-border transition-colors cursor-pointer"
      aria-label="{{ 'MODAL.CLOSE' | translate }}">
      <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>

  <!-- Modal Body (Scrollable) -->
  <div class="overflow-y-auto px-5 py-4 space-y-5 flex-1">
    @if (!isSellable()) {
    <div
      class="flex items-center gap-2.5 p-3 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-300 text-xs font-medium">
      <svg class="w-4 h-4 shrink-0 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" />
      </svg>
      <span>{{ 'PRODUCT.UNAVAILABLE_ALERT' | translate }}</span>
    </div>
    }

    <div class="space-y-5" [class.opacity-50]="!isSellable()" [class.pointer-events-none]="!isSellable()">
      @for (group of groupedOptions(); track group.group.id) {
      <div class="flex flex-col gap-2.5">
        <!-- Group Header -->
        <div class="flex items-center justify-between border-b border-border/70 pb-2">
          <div class="flex items-center gap-2">
            <h3 class="text-sm font-bold text-main">
              {{ getDisplayText(group.group.Name) }}
            </h3>
            @if (getGroupMinQuantity(group.group, group.options) > 0) {
            <span class="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
              {{ 'MODAL.MANDATORY' | translate }}
            </span>
            }
          </div>

          <span class="text-[11px] text-muted">
            @if (getGroupMinQuantity(group.group, group.options) > 0 && getGroupMaxQuantity(group.group, group.options) > 1) {
            {{ 'MODAL.SELECT_BETWEEN' | translate : { min: getGroupMinQuantity(group.group, group.options), max: getGroupMaxQuantity(group.group, group.options) } }}
            } @else if (getGroupMinQuantity(group.group, group.options) > 0 && getGroupMaxQuantity(group.group, group.options) === 1) {
            {{ 'MODAL.SELECT' | translate : { num: 1 } }}
            } @else if (getGroupMaxQuantity(group.group, group.options) > 1) {
            {{ 'MODAL.SELECT_UP_TO' | translate : { num: getGroupMaxQuantity(group.group, group.options) } }}
            }
          </span>
        </div>

        <!-- Options in this Group -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
          @for (option of group.options; track getOptionKey(option)) {
          <div (click)="toggleOption(group.group, option, group.options)" class="flex items-center justify-between p-2.5 rounded-xl border transition-all select-none"
            [class.cursor-pointer]="!isStepperGroup(group.group, group.options)"
            [class.border-primary]="isOptionSelected(option) && resolveType(option, group.group) !== OptionType.REMOVABLE"
            [class.bg-primary/5]="isOptionSelected(option) && resolveType(option, group.group) !== OptionType.REMOVABLE"
            [class.border-red-400]="isOptionSelected(option) && resolveType(option, group.group) === OptionType.REMOVABLE"
            [class.bg-red-500/5]="isOptionSelected(option) && resolveType(option, group.group) === OptionType.REMOVABLE" [class.border-border]="!isOptionSelected(option)"
            [class.bg-surface]="!isOptionSelected(option)" [class.hover:border-primary/40]="!isOptionSelected(option)">

            <div class="flex items-center gap-2 min-w-0">
              <!-- Indicator for non-stepper groups -->
              @if (!isStepperGroup(group.group, group.options)) {
              <div class="w-4 h-4 flex items-center justify-center border transition-colors shrink-0" [class.rounded-full]="isRadioGroup(group.group, group.options)"
                [class.rounded]="!isRadioGroup(group.group, group.options)"
                [class.border-primary]="isOptionSelected(option) && resolveType(option, group.group) !== OptionType.REMOVABLE"
                [class.bg-primary]="isOptionSelected(option) && resolveType(option, group.group) !== OptionType.REMOVABLE"
                [class.border-red-500]="isOptionSelected(option) && resolveType(option, group.group) === OptionType.REMOVABLE"
                [class.bg-red-500]="isOptionSelected(option) && resolveType(option, group.group) === OptionType.REMOVABLE" [class.border-border]="!isOptionSelected(option)">
                @if (isOptionSelected(option)) {
                <svg class="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd" />
                </svg>
                }
              </div>
              }

              <div class="flex items-center gap-1.5 min-w-0">
                @if (resolveType(option, group.group) === OptionType.REMOVABLE) {
                <span class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 shrink-0">
                  [{{ 'OPTION.REMOVED_PREFIX' | translate }}]
                </span>
                }

                <span class="text-xs font-medium text-main truncate" [class.line-through]="resolveType(option, group.group) === OptionType.REMOVABLE && isOptionSelected(option)"
                  [class.text-muted]="resolveType(option, group.group) === OptionType.REMOVABLE && isOptionSelected(option)">
                  {{ getDisplayText(option.Name) }}
                </span>
              </div>
            </div>

            <!-- Right side: Multiplier / Price Tag / Stepper -->
            <div class="flex items-center gap-2 shrink-0 ml-2">
              <!-- MULTIPLY display -->
              @if (resolveType(option, group.group) === OptionType.MULTIPLY) {
              <span class="text-xs font-semibold text-primary">
                {{ getMultiplierLabel(option) }}
              </span>
              }

              <!-- ADD / PRODUCT modifier price tag (for single-choice groups) -->
              @if (!isStepperGroup(group.group, group.options) && (resolveType(option, group.group) === OptionType.ADD || resolveType(option, group.group) === OptionType.PRODUCT)
              &&
              option.Modifier && option.Modifier > 0) {
              <span class="text-xs font-semibold text-primary">
                +{{ option.Modifier * (1 + productEffectiveTaxPercent() / 100) | formatPrice:currencyPrecision:companyCurrency }}
              </span>
              }

              <!-- Stepper controls for ADD / PRODUCT with max > 1 -->
              @if (isStepperGroup(group.group, group.options)) {
              <div class="flex items-center gap-1.5">
                @if (option.Modifier && option.Modifier > 0) {
                <span class="text-xs font-semibold text-primary mr-1">
                  +{{ option.Modifier * (1 + productEffectiveTaxPercent() / 100) | formatPrice:currencyPrecision:companyCurrency }}
                </span>
                }

                <div class="flex items-center rounded-lg border border-border bg-background">
                  <button type="button" (click)="decreaseOptionQuantity(group.group, option, $event)" [disabled]="getOptionQuantity(option) === 0"
                    class="w-7 h-7 flex items-center justify-center text-muted hover:text-main hover:bg-surface-hover rounded-l-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Decrease option quantity">
                    <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M20 12H4" />
                    </svg>
                  </button>
                  <span class="w-6 text-center text-xs font-bold text-main select-none">
                    {{ getOptionQuantity(option) }}
                  </span>
                  <button type="button" (click)="increaseOptionQuantity(group.group, option, group.options, $event)"
                    [disabled]="!canIncreaseOption(group.group, option, group.options)"
                    class="w-7 h-7 flex items-center justify-center text-muted hover:text-main hover:bg-surface-hover rounded-r-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Increase option quantity">
                    <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>
              }
            </div>
          </div>
          }
        </div>
      </div>
      } @empty {
      <p class="text-muted text-xs py-2">{{ 'MODAL.NO_OPTIONS' | translate }}</p>
      }

      <!-- Special instructions -->
      <div class="pt-2 border-t border-border">
        <label class="block text-xs font-semibold text-main mb-1">
          {{ 'PRODUCT.SPECIAL_INSTRUCTIONS' | translate }}
        </label>
        <input type="text" [(ngModel)]="notes" [disabled]="!isSellable()" placeholder="{{ 'PRODUCT.SPECIAL_INSTRUCTIONS_PLACEHOLDER' | translate }}"
          class="w-full text-xs rounded-xl border border-border bg-background p-2 text-main placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed" />
      </div>
    </div>
  </div>

  <!-- Modal Footer with Stepper & Add to Cart -->
  <div class="px-5 py-3.5 border-t border-border bg-surface shrink-0 flex items-center gap-3">
    <!-- Quantity Stepper -->
    <div class="flex items-center rounded-xl border border-border bg-background shadow-2xs shrink-0" [class.opacity-50]="!isSellable()">
      <button type="button" (click)="decreaseQuantity()" [disabled]="!isSellable()"
        class="w-9 h-9 flex items-center justify-center text-muted hover:text-main hover:bg-surface-hover rounded-l-xl transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Decrease quantity">
        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M20 12H4" />
        </svg>
      </button>
      <span class="w-8 text-center text-xs font-bold text-main select-none">
        {{ quantity() }}
      </span>
      <button type="button" (click)="increaseQuantity()" [disabled]="!isSellable()"
        class="w-9 h-9 flex items-center justify-center text-muted hover:text-main hover:bg-surface-hover rounded-r-xl transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Increase quantity">
        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>

    <!-- Add Button -->
    <button type="button" (click)="onAddToCart()" [disabled]="!isSelectionValid() || !isSellable()"
      class="flex-1 py-2.5 px-4 rounded-xl font-bold text-xs shadow-md transition-all flex items-center justify-between cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      [class.bg-muted/40]="!isSellable()" [class.text-muted]="!isSellable()" [class.bg-primary]="isSellable()" [class.hover:bg-primary/90]="isSellable()"
      [class.text-primary-foreground]="isSellable()">
      @if (!isSellable()) {
      <div class="flex items-center gap-1.5">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
        <span>{{ 'PRODUCT.UNAVAILABLE' | translate }}</span>
      </div>
      } @else {
      <span>{{ 'PRODUCT.ADD_TO_CART' | translate }}</span>
      }
      <span>{{ totalPriceWithTax() | formatPrice:currencyPrecision:companyCurrency }}</span>
    </button>
  </div>
</div>